import styles from "./BookingWidget.module.scss";

import { useCallback, useEffect, useMemo, useRef } from "react";

import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getCoachAvailabilityQuery } from "@/modules/coaching/shared/queries/getCoachAvailabilityQuery";
import { getCoachMatchingAvailabilityQuery } from "@/modules/coaching/shared/queries/getCoachMatchingAvailabilityQuery";
import { useIsMobile } from "@/modules/shared/hooks/useIsMobile";
import {
  BOOKING_EVENTS,
  RESCHEDULE_EVENTS,
} from "@/modules/tracking/consts/Events";
import { trackBookingEvent } from "@/modules/tracking/utils/trackBookingEvent";

import {
  BookingKinds,
  CreateSessionStatuses,
  useBookingContext,
  WidgetSteps,
} from "../../context/BookingContext";
import { SLOT_INTERVAL, SLOT_SIZE } from "../../conts/sessionConsts";
import { useBookingIframeVars } from "../../hooks/useBookingIframeVars";
import { useLocalizedTimezoneOptions } from "../../hooks/useLocalizedTimezoneOptions";
import { useNativeBookingEvents } from "../../hooks/useNativeBookingEvents";
import { useTimezonedSpots } from "../../hooks/useTimezonedTimeSlots";
import { buildBookingEventProps } from "../../utils/buildBookingEventProps";
import { exitScreenForStep } from "../../utils/exitScreenForStep";
import { getBrowserTimezone } from "../../utils/getBrowserTimezone";
import { AvailabilityErrorPanel } from "../AvailabilityErrorPanel";
import { BookingFailedPanel } from "../BookingFailedPanel";
import { ConfirmationPanel } from "../ConfirmationPanel";
import { ConfirmDetailsPanel } from "../ConfirmDetailsPanel";
import { DatePickerSection } from "../DatePickerSection";
import { DetailsSection } from "../DetailsSection";
import { MessageCoachPanel } from "../MessageCoachPanel";
import { NeedHelpLink } from "../NeedHelpLink";
import { TimeSlotsSection } from "../TimeSlotsSection/TimeSlotsSection";

export const BookingWidget = ({
  compact = false,
  enableIframeVars = false,
  supportRoute,
}: {
  compact?: boolean;
  enableIframeVars?: boolean;
  supportRoute?: string;
} = {}) => {
  const { t } = useTranslation();

  const widgetContainerRef = useRef<HTMLDivElement>(null);

  const {
    coachId,
    coachName,
    setCoachData,
    setTimezoneOption,
    timezone,
    availabilityInterval,
    setCustomerId,
    setMemberPrefill,
    setRescheduleSessionId,
    handleBookingWidgetCta,
    currentStep,
    selectedDate,
    selectedDateTime,
    createSessionStatus,
    memberName,
    memberEmail,
    isRescheduling,
    entryPoint,
    cleanBookingState,
    completedSessionData,
    rescheduleSessionId,
    originalSessionStart,
    setTrialInfo,
    bookingKind,
    serviceName,
    setBookedByUserId,
    setPreSessionNotesEnabled,
    setMultiSessionBookingEnabled,
  } = useBookingContext();

  const isCoachMatchingServiceBooking =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE;

  const eventProps = useMemo(
    () =>
      buildBookingEventProps({
        isRescheduling,
        isCoachMatchingServiceBooking,
        coachId,
        serviceName,
        rescheduleSessionId,
      }),
    [
      isRescheduling,
      isCoachMatchingServiceBooking,
      coachId,
      serviceName,
      rescheduleSessionId,
    ],
  );

  // Build the payload for native postMessage events. previousSessionId always
  // reflects the session being rescheduled so the host receives it on both
  // success and error transitions.
  const nativeBookingPayload = useMemo(
    () => ({
      sessionId: completedSessionData?.sessionId,
      sessionStartTime: completedSessionData?.sessionStartTime,
      previousSessionId: rescheduleSessionId,
    }),
    [
      completedSessionData?.sessionId,
      completedSessionData?.sessionStartTime,
      rescheduleSessionId,
    ],
  );

  const isMobile = useIsMobile();

  const availabilityQuery = useMemo(() => {
    if (isCoachMatchingServiceBooking) {
      return getCoachMatchingAvailabilityQuery({
        endDate: availabilityInterval.end,
        startDate: availabilityInterval.start,
      });
    }

    return getCoachAvailabilityQuery({
      coachId: coachId || 0,
      slotSize: SLOT_SIZE,
      slotInterval: SLOT_INTERVAL,
      endDate: availabilityInterval.end,
      startDate: availabilityInterval.start,
    });
  }, [
    availabilityInterval.end,
    availabilityInterval.start,
    coachId,
    isCoachMatchingServiceBooking,
  ]);

  const result = useQueries({
    queries: [availabilityQuery],
  });

  const [availabilityResult] = result;

  const { refetch: refetchAvailability } = availabilityResult;

  const handleAvailabilityRetry = useCallback(() => {
    refetchAvailability();
  }, [refetchAvailability]);

  const coachAvailability = useMemo(() => {
    if (!availabilityResult.data) {
      return [];
    }

    return availabilityResult.data;
  }, [availabilityResult.data]);

  const timezonedSpots = useTimezonedSpots({ coachAvailability });

  const filteredTimeSpots = useMemo(() => {
    if (availabilityResult.isLoading || !selectedDate) {
      return [];
    }

    return timezonedSpots[selectedDate] || [];
  }, [availabilityResult.isLoading, selectedDate, timezonedSpots]);

  // Rehydrates embed-only coach/customer identity and member prefill values.
  // Keep this opt-in so stale iframe sessionStorage cannot affect public flows.
  useBookingIframeVars({
    enabled: enableIframeVars,
    setCoachData,
    setCustomerId,
    setMemberPrefill,
    cleanBookingState,
    setRescheduleSessionId,
    setTrialInfo,
    setBookedByUserId,
    setPreSessionNotesEnabled,
    setMultiSessionBookingEnabled,
  });

  useNativeBookingEvents(createSessionStatus, nativeBookingPayload);

  const hasTrackedStart = useRef(false);
  // eslint-disable-next-line react-hooks/purity
  const flowStartTime = useRef<number>(Date.now());
  useEffect(() => {
    if (hasTrackedStart.current || (!isCoachMatchingServiceBooking && !coachId))
      return;

    hasTrackedStart.current = true;
    flowStartTime.current = Date.now();

    if (isRescheduling) {
      trackBookingEvent(RESCHEDULE_EVENTS.RESCHEDULE_STARTED, {
        ...eventProps,
        original_session_start_time: originalSessionStart ?? null,
        known_member: Boolean(memberName || memberEmail),
        entry_point: entryPoint,
      });
      return;
    }

    trackBookingEvent(BOOKING_EVENTS.BOOKING_STARTED, {
      ...eventProps,
      known_member: Boolean(memberName || memberEmail),
      entry_point: entryPoint,
    });
  }, [
    coachId,
    memberName,
    memberEmail,
    isRescheduling,
    entryPoint,
    originalSessionStart,
    isCoachMatchingServiceBooking,
    eventProps,
  ]);

  // beforeunload fires on tab close, navigation away, and iframe unload.
  const bookingCompletedRef = useRef(false);
  useEffect(() => {
    if (createSessionStatus === "success") {
      bookingCompletedRef.current = true;
    }
  }, [createSessionStatus]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (bookingCompletedRef.current) return;

      const currentScreen = exitScreenForStep(currentStep);

      const eventName = isRescheduling
        ? RESCHEDULE_EVENTS.RESCHEDULE_EXITED
        : BOOKING_EVENTS.BOOKING_EXITED;

      trackBookingEvent(eventName, {
        current_screen: currentScreen,
        time_in_flow_seconds: Math.round(
          (Date.now() - flowStartTime.current) / 1000,
        ),
        had_selected_time_slot: Boolean(selectedDateTime),
        exit_trigger: "page_unload",
        ...eventProps,
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStep, selectedDateTime, isRescheduling, eventProps]);

  const localizedTimezoneOptions = useLocalizedTimezoneOptions(timezone?.value);

  useEffect(() => {
    if (timezone) {
      return; // persisted timezone already set — don't override on refresh
    }

    const browserTimezone = getBrowserTimezone();
    const timezoneFromBrowser = localizedTimezoneOptions.find(
      (option) => option.value === browserTimezone,
    );

    if (timezoneFromBrowser) {
      setTimezoneOption(timezoneFromBrowser);
    }
  }, [localizedTimezoneOptions, setTimezoneOption, timezone]);

  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (prevStepRef.current === currentStep) return;
    prevStepRef.current = currentStep;

    if (
      currentStep === WidgetSteps.SELECT_TIME &&
      !availabilityResult.isLoading
    ) {
      const eventName = isRescheduling
        ? RESCHEDULE_EVENTS.TIME_SLOTS_VIEWED
        : BOOKING_EVENTS.TIME_SLOTS_VIEWED;
      trackBookingEvent(eventName, {
        ...eventProps,
        slots_visible_count: filteredTimeSpots.length,
      });
    }

    // Booking-only step (reschedules skip it), so no reschedule variant.
    if (currentStep === WidgetSteps.MESSAGE_COACH) {
      trackBookingEvent(BOOKING_EVENTS.MESSAGE_COACH_VIEWED, {
        ...eventProps,
        entry_point: entryPoint,
      });
    }

    if (currentStep === WidgetSteps.SHOW_SUMMARY) {
      const eventName = isRescheduling
        ? RESCHEDULE_EVENTS.CONFIRMATION_VIEWED
        : BOOKING_EVENTS.CONFIRMATION_VIEWED;
      trackBookingEvent(eventName, {
        ...eventProps,
        prefilled_fields_count: [memberName, memberEmail].filter(Boolean)
          .length,
        entry_point: entryPoint,
      });
    }
  }, [
    currentStep,
    filteredTimeSpots.length,
    availabilityResult.isLoading,
    memberName,
    memberEmail,
    isRescheduling,
    entryPoint,
    eventProps,
  ]);

  const thirdColumnClassName = useMemo(() => {
    return `${styles.thirdColumn} ${selectedDate ? styles.selectedDate : ""}`;
  }, [selectedDate]);

  const isSinglePanelStep =
    currentStep === WidgetSteps.SHOW_SUMMARY ||
    currentStep === WidgetSteps.MESSAGE_COACH;

  const widgetContainerClassName = useMemo(() => {
    return [
      styles.widgetContainer,
      currentStep === WidgetSteps.SELECT_TIME ? styles.overlayOpen : "",
      isSinglePanelStep ? styles.summary : "",
      compact ? styles.compact : "",
    ]
      .filter(Boolean)
      .join(" ");
  }, [compact, currentStep, isSinglePanelStep]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    if (currentStep === WidgetSteps.SELECT_TIME) {
      widgetContainerRef.current?.scrollTo({ top: 0 });
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      return;
    }

    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }, [currentStep, isMobile, selectedDate]);

  if (createSessionStatus === CreateSessionStatuses.SUCCESS) {
    return <ConfirmationPanel compact={compact} />;
  }

  if (createSessionStatus === CreateSessionStatuses.ERROR) {
    return <BookingFailedPanel compact={compact} />;
  }

  if (availabilityResult.errorUpdateCount > 0 && !availabilityResult.data) {
    return (
      <AvailabilityErrorPanel
        isRepeatFailure={availabilityResult.errorUpdateCount > 1}
        isRetrying={availabilityResult.isFetching}
        onRetry={handleAvailabilityRetry}
        supportRoute={supportRoute}
      />
    );
  }

  const isSummaryStep = currentStep === WidgetSteps.SHOW_SUMMARY;
  const isMessageCoachStep = currentStep === WidgetSteps.MESSAGE_COACH;
  const showPicker = !isSummaryStep && !isMessageCoachStep;

  return (
    <div className={widgetContainerClassName} ref={widgetContainerRef}>
      <div className={styles.detailsSection}>
        <div className={styles.header}>
          <span className={styles.title}>
            {isCoachMatchingServiceBooking
              ? t("bookingWidget.coachMatchingService.title")
              : t("bookingWidget.title")}
          </span>
          <span className={styles.coachName}>
            {isCoachMatchingServiceBooking
              ? t("bookingWidget.coachMatchingService.description")
              : t("bookingWidget.coachLabel", { name: coachName })}
          </span>

          <DetailsSection
            timezone={timezone}
            localizedTimezoneOptions={localizedTimezoneOptions}
            setTimezoneOption={setTimezoneOption}
            disableTimezoneSelection={isSinglePanelStep}
          />
        </div>

        <NeedHelpLink to={supportRoute} />
      </div>

      <div className={styles.divider} />

      {showPicker && (
        <>
          <DatePickerSection
            isLoading={availabilityResult.isLoading}
            timezonedSpots={timezonedSpots}
          />

          <div className={thirdColumnClassName}>
            <div className={styles.divider} />
            <TimeSlotsSection filteredTimeSpots={filteredTimeSpots} />
          </div>
        </>
      )}

      {isMessageCoachStep && (
        <div className={styles.confirmDetailsPanel}>
          <MessageCoachPanel supportRoute={supportRoute} />
        </div>
      )}

      {isSummaryStep && (
        <div className={styles.confirmDetailsPanel}>
          <ConfirmDetailsPanel
            onSchedule={handleBookingWidgetCta}
            supportRoute={supportRoute}
          />
        </div>
      )}
      {showPicker && <NeedHelpLink mobileLink to={supportRoute} />}
    </div>
  );
};
